import type { Link as LinkSchema } from './generated/schemas'

const linkTypes = ['internal', 'external'] as const

type CtaLinkType = (typeof linkTypes)[number]

const normalizeLinkType = (value: unknown): CtaLinkType | undefined => {
  if (typeof value !== 'string') return undefined

  const normalized = value.trim().toLowerCase()

  if (linkTypes.includes(normalized as CtaLinkType)) {
    return normalized as CtaLinkType
  }

  return undefined
}

const getLinkFields = (link: LinkSchema | undefined) =>
  link?.fields as Record<string, unknown> | undefined

export const getCtaLinkType = (link: LinkSchema | undefined) =>
  normalizeLinkType(getLinkFields(link)?.linkType)

const getForceNewWindow = (link: LinkSchema | undefined) => {
  const value = getLinkFields(link)?.forceNewWindow
  return typeof value === 'boolean' ? value : undefined
}

/**
 * Whether a CTA renders as a button. `type` is the editor's choice of control —
 * `variant` only styles whichever control `type` selected, so it must never be
 * what decides this.
 */
export const isCtaButton = (link: LinkSchema | undefined) => {
  const type = getLinkFields(link)?.type
  return type === 'button' || type === 'submit'
}

type CtaLinkStyleVariant = NonNullable<LinkSchema['fields']['variant']>

/**
 * Map a Contentful `variant` onto a `<Link>` text-link style, for CTAs whose
 * `type` is `link`. The button variants have no text-link equivalent, so
 * anything unrecognized falls back to `standalone`.
 */
export const getCtaTextLinkVariant = (
  variant: CtaLinkStyleVariant | undefined
) => {
  if (variant === 'secondary-link') return 'secondary' as const
  if (variant === 'primary-accordion-link') return 'accordion' as const
  if (variant === 'primary-breadcrumb-link') return 'breadcrumb' as const
  return 'standalone' as const
}

export type CtaLinkBehavior = {
  showExternalIcon: boolean
  openInNewTab: boolean
}

export const getCtaLinkBehavior = (
  link: LinkSchema | undefined
): CtaLinkBehavior => {
  const linkType = getCtaLinkType(link)
  const showExternalIcon = linkType === 'external'

  const forceNewWindow = getForceNewWindow(link)
  const openInNewTab =
    forceNewWindow !== undefined ? forceNewWindow : showExternalIcon

  return { showExternalIcon, openInNewTab }
}
